import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';

import { chargebeeUtils } from '../utils';
import {
  AppchargeErrorType,
  ESdkEvents,
  FinalizeSubscriptionStatus,
} from '../../../../contracts';
import { sendFEEvent } from '../../../../eventUtil';
import { useAPI } from '../../../../services/api.service';
import { useCheckout } from '../../../../hooks/use-checkout';
import { useAppConfig } from '../../../../hooks/use-app-config';
import { ECheckoutStatus } from '../../../../constants/enums';
import { logError, logInfo } from '../../../../utils/coralogix-rum';
import { useSubscriptionPolling } from './use-subscription-polling';
import { usePaymentState } from '../../../Checkout/hooks/use-payment-state';
import { generateAppchargeErrorMessage } from '../../../../utils/generate-appcharge-error-message';
import { ChargebeeComponent, ChargebeeInstance } from '../../../../types/chargebee';
import { useShowTaxCalculationFields } from '../../../Checkout/hooks/use-show-tax-calculation-fields';
import { useAddressElementRef } from '../../../Checkout/components/AddressElement/hooks/use-address-element-ref';

const CHARGEBEE_PAYMENT_COMPONENT_TYPE = 'payment';
const CHARGEBEE_CARD_PAYMENT_METHOD = 'card';
const CHARGEBEE_INITIALIZATION_STARTED_LOG = 'Chargebee payment component initialization started';
const CHARGEBEE_MOUNTED_LOG = 'Chargebee payment component mounted';
const CHARGEBEE_PAYMENT_METHOD_CHANGED_LOG = 'Chargebee payment method changed';
const CHARGEBEE_CARD_CONFIRM_VALIDATION_LOG = 'Chargebee card confirm validation completed';
const CHARGEBEE_CARD_CONFIRM_STARTED_LOG = 'Chargebee card confirm started';
const CHARGEBEE_SUBSCRIPTION_PROCESSING_STARTED_LOG = 'Chargebee subscription processing started';
const CHARGEBEE_FINALIZE_STARTED_LOG = 'Chargebee subscription finalize started';
const CHARGEBEE_FINALIZE_SUCCEEDED_LOG = 'Chargebee subscription finalize succeeded';
const CHARGEBEE_INITIALIZATION_ERROR_LOG = 'Chargebee payment component initialization failed';
const CHARGEBEE_PAYMENT_ERROR_LOG = 'Chargebee subscription payment failed';
const CHARGEBEE_FINALIZE_ERROR_LOG = 'Chargebee subscription finalize failed';
const CHARGEBEE_PAYMENT_BLOCKED_LOG = 'Chargebee subscription payment start blocked';
const CHARGEBEE_INTENT_REFRESHED_LOG = 'Chargebee subscription payment intent refreshed';

export function useChargebeePaymentElement() {
  const ref = useRef<HTMLDivElement>(null);
  const lastAppliedPriceTotalRef = useRef<number | undefined>(undefined);
  const { API } = useAPI();
  const [showPayButton, setShowPayButton] = useState(false);
  const [paymentComponent, setPaymentComponent] = useState<ChargebeeComponent | null>(null);
  const setIsPaymentElementLoading = usePaymentState((state) => state.setIsPaymentElementLoading);
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const setCheckoutStatus = usePaymentState((state) => state.setCheckoutStatus);
  const priceBreakdown = usePaymentState((state) => state.priceBreakdown);
  const zipCode = usePaymentState((state) => state.zipCode);
  const customerGeoState = usePaymentState((state) => state.customerGeoState);
  const { checkoutData, subscriptionData, getEventParams } = useCheckout();
  const { publisherId } = useAppConfig();
  const { AddressElementRef } = useAddressElementRef();
  const { useTaxCalculationField } = useShowTaxCalculationFields();
  const { startSubscriptionPolling } = useSubscriptionPolling();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { showBoundary } = useErrorBoundary();
  const email = checkoutData?.customer?.email;
  const plan = subscriptionData?.plan;
  const paymentIntent = subscriptionData?.paymentIntent;
  const chargebeeConfig = checkoutData?.pks?.chargebee;

  useEffect(() => {
    let mountedPaymentComponent: ChargebeeComponent | null = null;

    async function initializeChargebee() {
      try {
        setShowPayButton(false);
        setPaymentComponent(null);
        logInfo(CHARGEBEE_INITIALIZATION_STARTED_LOG, getChargebeeLogData());
        const chargebee: ChargebeeInstance = window.Chargebee.init({
          site: chargebeeConfig?.siteId || '',
          publishableKey: chargebeeConfig?.publicKey || '',
        });

        const components = chargebee.components(chargebeeUtils.getComponentsOptions());

        const paymentComponent = components.create(
          CHARGEBEE_PAYMENT_COMPONENT_TYPE,
          chargebeeUtils.getPaymentComponentOptions(paymentIntent, email),
          {
            onError: (error: any) => {
              handleError(error);
            },
            onSuccess: async () => {
              startSubscriptionProcessing();
              try {
                logInfo(CHARGEBEE_FINALIZE_STARTED_LOG, getChargebeeLogData());
                const finalizeSubscriptionResponse = await API.finalizeSubscriptionPayment({
                  params: {
                    planId: plan?._id,
                  },
                  customer: {
                    id: checkoutData?.customer?.id,
                    email: checkoutData?.customer?.email,
                  },
                });
                if (finalizeSubscriptionResponse.status === FinalizeSubscriptionStatus.SUCCESS) {
                  logInfo(CHARGEBEE_FINALIZE_SUCCEEDED_LOG, {
                    ...getChargebeeLogData(),
                    subscriptionId: finalizeSubscriptionResponse.subscriptionId,
                    finalizeSubscriptionStatus: finalizeSubscriptionResponse.status,
                  });
                  startSubscriptionPolling(finalizeSubscriptionResponse.subscriptionId);
                  sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
                    ...getEventParams(),
                  });
                  return;
                }

                handleFinalizeError(new Error(CHARGEBEE_FINALIZE_ERROR_LOG), {
                  finalizeSubscriptionStatus: finalizeSubscriptionResponse.status,
                });
              } catch (error: any) {
                handleFinalizeError(error, {
                  finalizeSubscriptionError: error?.response?.data,
                });
              }
            },
            onPaymentMethodChange: (paymentMethod: string) => {
              setShowPayButton(paymentMethod === CHARGEBEE_CARD_PAYMENT_METHOD);
              setIsPaymentElementLoading(false);
              logInfo(CHARGEBEE_PAYMENT_METHOD_CHANGED_LOG, {
                ...getChargebeeLogData(),
                paymentMethod,
                showPayButton: paymentMethod === CHARGEBEE_CARD_PAYMENT_METHOD,
              });
            },
            onButtonClick: async () => {
              const isAddressValid = await validateAddress();
              if (isAddressValid) {
                return;
              }
              logInfo(CHARGEBEE_PAYMENT_BLOCKED_LOG, getChargebeeLogData());
              return Promise.reject();
            },
          }
        );
        mountedPaymentComponent = paymentComponent;
        setPaymentComponent(paymentComponent);

        paymentComponent.mount(ref.current);
        lastAppliedPriceTotalRef.current = priceBreakdown?.priceTotal;
        logInfo(CHARGEBEE_MOUNTED_LOG, getChargebeeLogData());
      } catch (error: any) {
        logError(CHARGEBEE_INITIALIZATION_ERROR_LOG, error, getChargebeeLogData());
        showBoundary(t('unableToOpenCheckout'));
      }
    }
    if (paymentIntent) {
      initializeChargebee();
    }

    return () => {
      mountedPaymentComponent?.unmount?.();
    };
  }, [paymentIntent, plan, chargebeeConfig]);

  useEffect(() => {
    if (!paymentComponent || priceBreakdown?.priceTotal === lastAppliedPriceTotalRef.current) {
      return;
    }
    lastAppliedPriceTotalRef.current = priceBreakdown?.priceTotal;
    // Card input must survive a tax recalc; selecting a wallet re-fetches its own amount.
    if (showPayButton) {
      return;
    }
    logInfo(CHARGEBEE_INTENT_REFRESHED_LOG, getChargebeeLogData());
    paymentComponent.update({ paymentIntent: { id: paymentIntent?.id } });
  }, [paymentComponent, showPayButton, priceBreakdown?.priceTotal]);

  async function handlePaymentButtonClick() {
    try {
      const isAddressValid = await validateAddress();
      if (!isAddressValid) {
        logInfo(CHARGEBEE_PAYMENT_BLOCKED_LOG, getChargebeeLogData());
        return;
      }

      const isValid = await paymentComponent?.validate();

      logInfo(CHARGEBEE_CARD_CONFIRM_VALIDATION_LOG, {
        ...getChargebeeLogData(),
        isPaymentValid: Boolean(isValid),
        isAddressValid,
      });

      if (isValid && isAddressValid) {
        setIsPaymentLoading(true);
        logInfo(CHARGEBEE_CARD_CONFIRM_STARTED_LOG, getChargebeeLogData());
        await paymentComponent?.confirm();
      }
    } catch (error: any) {
      handleError(error);
    }
  }

  async function validateAddress() {
    if (!useTaxCalculationField) {
      return true;
    }

    await AddressElementRef.current?.validate();
    return Boolean(AddressElementRef.current?.isValid());
  }

  function startSubscriptionProcessing() {
    setErrorMessage('');
    setIsPaymentLoading(true);
    setCheckoutStatus(ECheckoutStatus.PENDING);
    logInfo(CHARGEBEE_SUBSCRIPTION_PROCESSING_STARTED_LOG, getChargebeeLogData());
  }

  function stopSubscriptionProcessing() {
    setIsPaymentLoading(false);
    setCheckoutStatus(ECheckoutStatus.INIT);
  }

  function handleError(error: any) {
    logError(CHARGEBEE_PAYMENT_ERROR_LOG, error, getChargebeeLogData());
    sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
      ...getEventParams(),
    });
    stopSubscriptionProcessing();
    setErrorMessage(generateAppchargeErrorMessage(t, AppchargeErrorType.GENERIC));
  }

  function handleFinalizeError(error: any, additionalData?: Record<string, any>) {
    logError(CHARGEBEE_FINALIZE_ERROR_LOG, error, {
      ...getChargebeeLogData(),
      ...additionalData,
    });
    sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
      ...getEventParams(),
    });
    stopSubscriptionProcessing();
    showBoundary(t('somethingWentWrong'));
  }

  function getChargebeeLogData() {
    const eventParams = getEventParams();
    const activePriceBreakdown =
      priceBreakdown ||
      subscriptionData?.firstPaymentDiscount?.priceBreakdown ||
      subscriptionData?.pricing?.priceBreakdown;
    const customer = checkoutData?.customer;

    return {
      publisherId,
      sessionId: eventParams.sessionId,
      planId: plan?._id,
      providerPlanId: plan?.providerPlanId,
      itemPriceId: plan?.itemPriceId,
      subscriptionCustomerId: checkoutData?.customer?.id,
      paymentIntentId: paymentIntent?.id,
      currencyCode: checkoutData?.pricing?.currencyCode,
      priceTotal: activePriceBreakdown?.priceTotal,
      priceNet: activePriceBreakdown?.priceNet,
      tax: activePriceBreakdown?.tax,
      taxRatePoints: activePriceBreakdown?.taxRatePoints,
      isTaxValid: activePriceBreakdown?.isTaxValid,
      isTaxInclusive: activePriceBreakdown?.inclusive,
      discount: activePriceBreakdown?.discount,
      discountRatePoints: activePriceBreakdown?.discountRatePoints,
      taxCountry: customer?.taxCountry,
      geoCountryCode: customer?.geoLocator?.countryCode2,
      geoState: customer?.geoLocator?.state,
      addressState: customer?.address?.state,
      calculatedTaxState: customerGeoState,
      hasZipCode: Boolean(zipCode || customer?.address?.zipCode || customer?.geoLocator?.zipCode),
      useTaxCalculationField,
    };
  }

  return {
    ref,
    showPayButton,
    paymentComponent,
    AddressElementRef,
    setIsPaymentLoading,
    handlePaymentButtonClick,
    setIsPaymentElementLoading,
  };
}
